import { ArrowUpRight } from "lucide-react";
import useReveal from "../../hooks/useReveal";
import { SERVICES, TARIFS } from "../../lib/data";

const ServiceCard = ({ service, idx }) => {
  const ref = useReveal();
  const layouts = [
    "lg:col-span-7 lg:row-span-2 aspect-[4/5] lg:aspect-auto lg:min-h-[640px]",
    "lg:col-span-5 aspect-[4/3] lg:aspect-auto lg:min-h-[310px]",
    "lg:col-span-5 aspect-[4/3] lg:aspect-auto lg:min-h-[310px]",
  ];
  const delays = ["reveal-delay-1", "reveal-delay-2", "reveal-delay-3"];
  return (
    <article
      ref={ref}
      data-testid={`service-card-${service.id}`}
      className={`group relative overflow-hidden zoom-wrap reveal ${delays[idx]} ${layouts[idx]}`}
    >
      <img
        src={service.image}
        alt={service.title}
        className="w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-zp-navy/85 via-zp-navy/20 to-transparent" />
      <div className="absolute inset-0 p-7 md:p-10 flex flex-col justify-end text-white">
        <p className="overline text-zp-lemon mb-3">{service.eyebrow}</p>
        <h3 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight">
          {service.title}
        </h3>
        <p className="mt-3 max-w-md text-white/85 text-sm md:text-base leading-relaxed font-light">
          {service.description}
        </p>
        <a
          href={service.href}
          data-testid={`service-cta-${service.id}`}
          className="mt-6 inline-flex items-center gap-2 text-sm tracking-widest uppercase font-medium text-zp-lemon hover:text-white transition-colors"
        >
          {service.cta}
          <ArrowUpRight size={16} />
        </a>
      </div>
    </article>
  );
};

export const Services = () => {
  const headRef = useReveal();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-zp-sand py-24 md:py-40 relative overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div
          ref={headRef}
          className="reveal grid lg:grid-cols-12 gap-8 items-end mb-16 md:mb-24"
        >
          <div className="lg:col-span-7">
            <p className="overline text-zp-navy/60 mb-6 flex items-center gap-3">
              <span className="dot" /> Ce que nous proposons
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-zp-navy">
              Trois plaisirs,
              <br />
              <span className="italic font-light">une même adresse.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-zp-muted text-base md:text-lg leading-relaxed font-light">
              Restaurant face à la mer, location de transats et parasols à
              l'heure ou à la journée, et un bar de plage pensé pour les
              longues fins d'après-midi. Tout, sans bouger les orteils du sable.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 md:gap-8">
          {SERVICES.map((service, idx) => (
            <ServiceCard key={service.id} service={service} idx={idx} />
          ))}
        </div>

        <div
          id="tarifs"
          data-testid="tarifs-block"
          className="mt-16 md:mt-24 grid lg:grid-cols-12 gap-10 items-center bg-white p-10 md:p-14"
        >
          <div className="lg:col-span-5">
            <p className="overline text-zp-navy/60 mb-4 flex items-center gap-3">
              <span className="dot" /> Tarifs
            </p>
            <h3 className="font-serif text-3xl md:text-4xl text-zp-navy leading-tight">
              Location de
              <br />
              <span className="italic font-light">transats & parasols</span>
            </h3>
            <p className="mt-5 text-zp-muted font-light leading-relaxed">
              Réservez par téléphone ou directement à votre arrivée. Service en
              sable, serviettes mises à disposition.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="divide-y divide-zp-line">
              {TARIFS.map((t) => (
                <li
                  key={t.label}
                  data-testid={`tarif-${t.label.split(' ')[0].toLowerCase()}`}
                  className="flex items-baseline justify-between py-5"
                >
                  <span className="font-serif text-xl md:text-2xl text-zp-navy">
                    {t.label}
                  </span>
                  <span className="font-serif text-2xl md:text-3xl text-zp-navy font-medium">
                    {t.price}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
